import { useState } from "react";
import { useParams } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { TeamIntro } from "./TeamIntro";
import { PostCard } from "./PostCard";
import { CTASection } from "./CTASection";
import { ExpiredPage } from "./ExpiredPage";
import { Loader2, Sparkles } from "lucide-react";

interface PostContent {
  text: string;
  hashtags?: string[];
}

interface PreviewData {
  companyName: string;
  companyCity: string | null;
  companyWebsite: string | null;
  contactName: string | null;
  segment: string | null;
  photoUrls: string[];
  posts: {
    instagram: PostContent | null;
    facebook: PostContent | null;
    linkedin: PostContent | null;
  };
  comments: {
    lina: string | null;
    max: string | null;
    emma: string | null;
    alex: string | null;
  };
}

export function PreviewPage() {
  const params = useParams();
  const slug = params.slug as string | undefined;
  const [isExpired, setIsExpired] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);

  const { data, isLoading, error } = useQuery<PreviewData>({
    queryKey: ["/api/preview", slug, "data"],
    queryFn: async () => {
      const url = `/api/preview/${slug}/data`;
      const response = await fetch(url);
      const result = await response.json();
      
      if (!response.ok) {
        if (response.status === 410) {
          setIsExpired(true);
          throw new Error("expired");
        }
        if (response.status === 404) {
          setIsNotFound(true);
          throw new Error("not_found");
        }
        throw new Error(result.error || "Failed to load preview");
      }
      
      return result.data as PreviewData;
    },
    enabled: !!slug,
    retry: false,
  });

  const ctaMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/preview/${slug}/cta`);
      return response;
    },
    onSuccess: (data: any) => {
      if (data.redirectUrl) {
        // Store referrer slug for VSL tracking
        try {
          if (slug) {
            sessionStorage.setItem("preview_referrer", slug);
          }
        } catch {
          // sessionStorage not available, continue without it
        }
        window.location.href = data.redirectUrl;
      }
    },
  });

  const handleCtaClick = () => {
    ctaMutation.mutate();
  };

  if (isExpired) {
    return <ExpiredPage notFound={false} />;
  }

  if (isNotFound) {
    return <ExpiredPage notFound={true} />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 dark:from-purple-950 dark:via-blue-950 dark:to-indigo-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Lade Vorschau...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return <ExpiredPage notFound={true} />;
  }

  const firstPhoto = data.photoUrls?.[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 dark:from-purple-950 dark:via-blue-950 dark:to-indigo-950">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <header className="text-center mb-12" data-testid="preview-header">
          <div className="flex justify-center mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 lg:w-14 lg:h-14 bg-primary rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-primary-foreground font-bold text-xl lg:text-2xl font-playfair">A</span>
              </div>
              <div className="text-left">
                <p className="text-lg lg:text-xl font-bold text-foreground">Social Media</p>
                <p className="text-sm lg:text-base text-primary font-semibold">A-Team</p>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <h1 className="text-3xl lg:text-4xl font-bold font-playfair">
              Hallo {data.companyName}!
            </h1>
            <p className="text-lg text-muted-foreground flex items-center justify-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Euer Social Media A-Team hat sich vorgestellt.
            </p>
          </div>
        </header>

        <TeamIntro />

        <section className="py-8" data-testid="section-posts">
          <div className="text-center mb-8">
            <h2 className="text-2xl lg:text-3xl font-bold font-playfair mb-2">
              Eure ersten 3 Posts
            </h2>
            <p className="text-muted-foreground">
              Fertig vorbereitet, nur noch freigeben
            </p>
          </div>

          <div className="grid gap-6">
            <PostCard 
              platform="instagram" 
              post={data.posts.instagram}
              comments={data.comments}
              photoUrl={firstPhoto}
            />
            <PostCard 
              platform="facebook" 
              post={data.posts.facebook}
              comments={data.comments}
            />
            <PostCard 
              platform="linkedin" 
              post={data.posts.linkedin}
              comments={data.comments}
            />
          </div>
        </section>

        <CTASection 
          companyName={data.companyName}
          onCtaClick={handleCtaClick}
          isLoading={ctaMutation.isPending}
        />

        <footer className="text-center py-8 border-t mt-8">
          <p className="text-xs text-muted-foreground">
            Diese Seite wurde speziell für {data.companyName} erstellt.
            {data.companyCity && ` Grüße aus ${data.companyCity}!`}
          </p>
        </footer>
      </div>
    </div>
  );
}
